import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useSubscription } from '@/hooks/useSubscription';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  MessageSquare, Bot, ShoppingCart, BarChart3, Zap, Shield,
  ArrowRight, Check, ChevronRight, Star, Send, Clock, CheckCheck,
  Package, Phone, TrendingUp, Users
} from 'lucide-react';

// Animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: 'easeOut' as const },
  }),
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: (i: number = 0) => ({
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, delay: i * 0.1, ease: 'easeOut' as const },
  }),
};

const slideRight = {
  hidden: { opacity: 0, x: 60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: 'easeOut' as const } },
};

const NAV_LINKS = [
  { label: 'Features', href: '#features' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Pricing', href: '#pricing' },
];

function Navbar() {
  const navigate = useNavigate();
  return (
    <motion.nav
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border/50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/shop')}>
          <div className="w-9 h-9 bg-primary rounded-xl flex items-center justify-center">
            <MessageSquare className="text-primary-foreground" size={20} />
          </div>
          <span className="text-lg font-bold text-foreground">WatiFlow</span>
        </div>
        <div className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map(l => (
            <a key={l.href} href={l.href} className="text-sm text-muted-foreground hover:text-foreground transition-colors">{l.label}</a>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/login')}>Login</Button>
          <Button size="sm" className="bg-gradient-to-r from-primary to-emerald-500 hover:opacity-90 shadow-lg shadow-primary/25" onClick={() => navigate('/signup')}>
            Get Started Free <ArrowRight size={14} className="ml-1" />
          </Button>
        </div>
      </div>
    </motion.nav>
  );
}

function HeroSection() {
  const navigate = useNavigate();
  return (
    <section className="pt-32 pb-20 px-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/3" />
      <div className="absolute top-20 right-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
      <div className="absolute bottom-10 left-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      <div className="max-w-7xl mx-auto relative">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            className="space-y-8"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div variants={fadeUp} custom={0} className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-sm text-primary font-medium">
              <Zap size={14} /> #1 WhatsApp COD Automation
            </motion.div>
            <motion.h1 variants={fadeUp} custom={1} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-foreground leading-tight">
              Automate Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-emerald-500">COD Orders</span> with WhatsApp
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-lg text-muted-foreground max-w-lg">
              Confirm orders, reduce fake deliveries, and automate customer communication — all through WhatsApp. Built for COD e-commerce.
            </motion.p>
            <motion.div variants={fadeUp} custom={3} className="flex flex-wrap gap-4">
              <Button size="lg" className="bg-gradient-to-r from-primary to-emerald-500 hover:opacity-90 shadow-xl shadow-primary/30 text-base px-8" onClick={() => navigate('/signup')}>
                Start Free Trial <ArrowRight size={18} className="ml-2" />
              </Button>
              <Button size="lg" variant="outline" className="text-base px-8" onClick={() => navigate('/login')}>
                Login
              </Button>
            </motion.div>
            <motion.div variants={fadeUp} custom={4} className="flex items-center gap-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-1"><Check size={16} className="text-primary" /> Free plan available</span>
              <span className="flex items-center gap-1"><Check size={16} className="text-primary" /> No credit card</span>
              <span className="flex items-center gap-1"><Check size={16} className="text-primary" /> Setup in 5 min</span>
            </motion.div>
          </motion.div>
          {/* Fake WhatsApp Preview */}
          <motion.div
            className="relative"
            initial="hidden"
            animate="visible"
            variants={slideRight}
          >
            <div className="bg-card rounded-3xl shadow-2xl border border-border/50 overflow-hidden max-w-sm mx-auto">
              <div className="bg-[hsl(var(--emerald-dark))] px-5 py-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-foreground/20 flex items-center justify-center">
                  <Phone size={18} className="text-primary-foreground" />
                </div>
                <div>
                  <p className="text-primary-foreground font-semibold text-sm">Your Store</p>
                  <p className="text-primary-foreground/95 text-xs">online</p>
                </div>
              </div>
              <div className="bg-[hsl(var(--background))] p-4 space-y-3 min-h-[320px]">
                <ChatBubble dir="in" text="Hi! I want to order the skin care set 🧴" time="10:02" delay={0.8} />
                <ChatBubble dir="out" text="Great choice! ✅ Please confirm your order:\n\n🛍 Skin Care Set\n💰 299 MAD\n🚚 Free delivery\n\nReply YES to confirm" time="10:02" ai delay={1.2} />
                <ChatBubble dir="in" text="YES ✅" time="10:03" delay={1.8} />
                <ChatBubble dir="out" text="✅ Order confirmed! Tracking: #WF-4821\nDelivery in 24-48h 🚚" time="10:03" ai delay={2.2} />
              </div>
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ delay: 2.6, duration: 0.5 }}
              className="absolute -bottom-4 -right-4 bg-card rounded-2xl shadow-xl border border-border/50 p-4 flex items-center gap-3"
            >
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Bot size={20} className="text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">AI Response</p>
                <p className="text-sm font-semibold text-foreground">Auto-confirmed ✅</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function ChatBubble({ dir, text, time, ai, delay = 0 }: { dir: 'in' | 'out'; text: string; time: string; ai?: boolean; delay?: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 15, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay, duration: 0.4, ease: 'easeOut' }}
      className={`flex ${dir === 'out' ? 'justify-end' : 'justify-start'}`}
    >
      <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm relative ${
        dir === 'out'
          ? 'bg-primary/15 text-foreground rounded-br-md'
          : 'bg-card border border-border rounded-bl-md text-foreground'
      }`}>
        {ai && <span className="text-[10px] text-[hsl(var(--emerald-dark))] font-semibold flex items-center gap-1 mb-1"><Bot size={10} /> AI</span>}
        <p className="whitespace-pre-line leading-relaxed">{text}</p>
        <span className="text-[10px] text-foreground/70 flex items-center justify-end gap-1 mt-1">
          {time} {dir === 'out' && <CheckCheck size={12} className="text-[hsl(var(--emerald-dark))]" />}
        </span>
      </div>
    </motion.div>
  );
}

const FEATURES = [
  { icon: MessageSquare, title: 'WhatsApp Automation', desc: 'Auto-reply to customers, confirm orders, and send updates — all on WhatsApp.' },
  { icon: ShoppingCart, title: 'COD Order Management', desc: 'Track orders from creation to delivery with automatic status sync.' },
  { icon: Bot, title: 'AI-Powered Replies', desc: 'Smart AI understands customer intent and responds in their language.' },
  { icon: Shield, title: 'Reduce Fake Orders', desc: 'Confirm every order via WhatsApp before shipping. Cut returns by 40%.' },
  { icon: BarChart3, title: 'Real-time Analytics', desc: 'Monitor delivery rates, response times, and conversion in one dashboard.' },
  { icon: Zap, title: 'Smart Follow-ups', desc: 'Automatic follow-ups for unconfirmed orders with customizable rules.' },
];

function FeaturesSection() {
  return (
    <section id="features" className="py-20 px-4 bg-secondary/30">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">Everything You Need to Automate COD</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">Powerful tools built specifically for cash-on-delivery e-commerce businesses.</p>
        </motion.div>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {FEATURES.map((f, i) => (
            <motion.div key={i} variants={scaleIn} custom={i}>
              <Card className="group hover:shadow-lg hover:border-primary/30 transition-all duration-300 bg-card h-full">
                <CardContent className="p-6 space-y-4">
                  <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <f.icon size={24} className="text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">{f.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

const STEPS = [
  { icon: Package, title: 'Connect Your Store', desc: 'Link your Shopify or WooCommerce store in minutes.' },
  { icon: Send, title: 'Orders Flow In', desc: 'New orders trigger automated WhatsApp confirmation messages.' },
  { icon: MessageSquare, title: 'Customer Confirms', desc: 'Customer replies YES on WhatsApp to confirm their order.' },
  { icon: TrendingUp, title: 'Auto Status Updates', desc: 'Order status syncs automatically — no manual work needed.' },
];

function HowItWorks() {
  return (
    <section id="how-it-works" className="py-20 px-4">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">How It Works</h2>
          <p className="text-lg text-muted-foreground">Get started in 4 simple steps</p>
        </motion.div>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {STEPS.map((s, i) => (
            <motion.div key={i} variants={fadeUp} custom={i} className="text-center space-y-4 relative">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-emerald-500 flex items-center justify-center mx-auto shadow-lg shadow-primary/20">
                <s.icon size={28} className="text-primary-foreground" />
              </div>
              <div className="text-xs font-bold text-primary uppercase tracking-wider">Step {i + 1}</div>
              <h3 className="text-lg font-semibold text-foreground">{s.title}</h3>
              <p className="text-sm text-muted-foreground">{s.desc}</p>
              {i < 3 && <ChevronRight size={20} className="text-muted-foreground/30 absolute top-8 -right-4 hidden lg:block" />}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

const BENEFITS = [
  { value: '40%', label: 'Less Fake Orders', desc: 'WhatsApp confirmation cuts unverified orders dramatically.' },
  { value: '3x', label: 'Faster Response', desc: 'AI replies instantly — no more waiting for manual responses.' },
  { value: '85%', label: 'Delivery Rate', desc: 'Confirmed orders have significantly higher delivery success.' },
  { value: '10h+', label: 'Saved Weekly', desc: 'Automate repetitive tasks and focus on growing your business.' },
];

function BenefitsSection() {
  return (
    <section className="py-20 px-4 bg-gradient-to-br from-primary/5 to-transparent">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">Results That Matter</h2>
          <p className="text-lg text-muted-foreground">Real impact for COD businesses</p>
        </motion.div>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {BENEFITS.map((b, i) => (
            <motion.div key={i} variants={scaleIn} custom={i}>
              <Card className="text-center bg-card hover:shadow-lg transition-shadow h-full">
                <CardContent className="p-8 space-y-3">
                  <div className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-primary to-emerald-500">{b.value}</div>
                  <h3 className="text-lg font-semibold text-foreground">{b.label}</h3>
                  <p className="text-sm text-muted-foreground">{b.desc}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

const TESTIMONIALS = [
  { name: 'Ahmed K.', role: 'E-commerce Owner, Morocco', text: 'WatiFlow cut our fake orders by half. The WhatsApp automation is a game-changer for COD.', rating: 5 },
  { name: 'Sara M.', role: 'Store Manager, Iraq', text: 'Setup took 10 minutes. Now every order gets confirmed automatically. Love it!', rating: 5 },
  { name: 'Omar B.', role: 'COD Business, Pakistan', text: 'The AI replies in Darija perfectly. Our customers think they\'re talking to a real person.', rating: 5 },
];

function TestimonialsSection() {
  return (
    <section className="py-20 px-4">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">Loved by COD Businesses</h2>
          <p className="text-lg text-muted-foreground">See what our users are saying</p>
        </motion.div>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
          className="grid sm:grid-cols-3 gap-6"
        >
          {TESTIMONIALS.map((t, i) => (
            <motion.div key={i} variants={fadeUp} custom={i}>
              <Card className="bg-card hover:shadow-lg transition-shadow h-full">
                <CardContent className="p-6 space-y-4">
                  <div className="flex gap-1">{Array.from({ length: t.rating }).map((_, j) => <Star key={j} size={16} className="fill-yellow-400 text-yellow-400" />)}</div>
                  <p className="text-sm text-foreground leading-relaxed">"{t.text}"</p>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.role}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

const PLANS = [
  { name: 'Free', price: '0', period: '/mo', features: ['100 messages/mo', '1 WhatsApp number', 'Basic automation', 'Order tracking'], cta: 'Start Free', popular: false },
  { name: 'Pro', price: '49', period: '/mo', features: ['5,000 messages/mo', '3 WhatsApp numbers', 'AI auto-replies', 'Advanced automation', 'Analytics dashboard', 'Priority support'], cta: 'Start Pro Trial', popular: true },
  { name: 'Enterprise', price: '149', period: '/mo', features: ['Unlimited messages', 'Unlimited numbers', 'Custom AI training', 'API access', 'Dedicated support', 'Custom integrations'], cta: 'Contact Sales', popular: false },
];

function PricingSection() {
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const { plan: currentPlan, loading: subLoading } = useSubscription();

  const handlePlanClick = async (plan: typeof PLANS[number]) => {
    if (plan.name === currentPlan) return;
    if (plan.name === 'Free') return navigate('/signup');
    if (plan.name === 'Enterprise') return navigate('/signup');
    try {
      setLoadingPlan(plan.name);
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { plan: plan.name },
      });
      if (error) throw error;
      if (data?.url) window.open(data.url, '_blank');
    } catch (e) {
      console.error('Checkout error:', e);
      alert('Checkout failed. Please try again.');
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <section id="pricing" className="py-20 px-4 bg-secondary/30">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">Simple, Transparent Pricing</h2>
          <p className="text-lg text-muted-foreground">Start free, scale as you grow</p>
        </motion.div>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
          className="grid sm:grid-cols-3 gap-6 max-w-5xl mx-auto"
        >
          {PLANS.map((p, i) => {
            const isCurrent = p.name === currentPlan;
            return (
            <motion.div key={i} variants={scaleIn} custom={i}>
              <Card className={`relative bg-card h-full ${isCurrent ? 'border-emerald-500 shadow-xl shadow-emerald-500/10 scale-105' : p.popular ? 'border-primary shadow-xl shadow-primary/10 scale-105' : 'hover:shadow-lg'} transition-all`}>
                {isCurrent ? (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-emerald-500 text-white text-xs font-bold rounded-full">
                    Current Plan
                  </div>
                ) : p.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-primary to-emerald-500 text-primary-foreground text-xs font-bold rounded-full">
                    Most Popular
                  </div>
                )}
                <CardContent className="p-8 space-y-6">
                  <div>
                    <h3 className="text-xl font-bold text-foreground">{p.name}</h3>
                    <div className="mt-2 flex items-baseline gap-1">
                      <span className="text-4xl font-extrabold text-foreground">${p.price}</span>
                      <span className="text-muted-foreground text-sm">{p.period}</span>
                    </div>
                  </div>
                  <ul className="space-y-3">
                    {p.features.map((f, j) => (
                      <li key={j} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Check size={16} className="text-primary shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <Button
                    className={`w-full ${p.popular && !isCurrent ? 'bg-gradient-to-r from-primary to-emerald-500 hover:opacity-90 shadow-lg shadow-primary/25' : ''}`}
                    variant={isCurrent ? 'secondary' : p.popular ? 'default' : 'outline'}
                    onClick={() => handlePlanClick(p)}
                    disabled={loadingPlan === p.name || isCurrent || subLoading}
                  >
                    {isCurrent ? 'Current Plan' : loadingPlan === p.name ? 'Loading...' : p.cta}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

function CTASection() {
  const navigate = useNavigate();
  return (
    <section className="py-20 px-4">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={scaleIn}
        className="max-w-4xl mx-auto text-center"
      >
        <div className="bg-gradient-to-br from-primary to-emerald-500 rounded-3xl p-12 sm:p-16 shadow-2xl shadow-primary/20 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
          <div className="relative space-y-6">
            <h2 className="text-3xl sm:text-4xl font-bold text-primary-foreground">Ready to Automate Your COD Business?</h2>
            <p className="text-primary-foreground/80 text-lg max-w-2xl mx-auto">
              Join hundreds of e-commerce businesses using WatiFlow to confirm orders, reduce returns, and grow revenue.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button size="lg" variant="secondary" className="text-base px-8 shadow-xl" onClick={() => navigate('/signup')}>
                Create Free Account <ArrowRight size={18} className="ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}

function Footer() {
  return (
    <motion.footer
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="border-t border-border/50 py-12 px-4"
    >
      <div className="max-w-7xl mx-auto flex flex-col gap-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-xl flex items-center justify-center">
              <MessageSquare className="text-primary-foreground" size={16} />
            </div>
            <span className="font-bold text-foreground">WatiFlow</span>
          </div>
          <p className="text-sm text-muted-foreground">© {new Date().getFullYear()} WatiFlow. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Users size={16} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">500+ businesses trust WatiFlow</span>
          </div>
        </div>
        <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 pt-4 border-t border-border/40 text-sm">
          <Link to="/privacy" className="text-muted-foreground hover:text-foreground transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="text-muted-foreground hover:text-foreground transition-colors">Terms of Service</Link>
          <Link to="/data-collection" className="text-muted-foreground hover:text-foreground transition-colors">Data Collection</Link>
        </nav>
      </div>
    </motion.footer>
  );
}

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const checkout = searchParams.get('checkout');
    if (!checkout) return;

    if (checkout === 'success') {
      toast.success('Payment successful! Updating your subscription...');
      supabase.functions.invoke('check-subscription').then(({ data, error }) => {
        if (error) {
          toast.error('Could not refresh subscription status.');
        } else if (data?.subscribed) {
          toast.success(`You are now on the ${data.plan} plan.`);
        } else {
          toast.message('Subscription not detected yet — it may take a moment.');
        }
      });
    } else if (checkout === 'cancel') {
      toast.error('Checkout canceled.');
    }

    searchParams.delete('checkout');
    setSearchParams(searchParams, { replace: true });
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main>
        <HeroSection />
        <FeaturesSection />
        <HowItWorks />
        <BenefitsSection />
        <TestimonialsSection />
        <PricingSection />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
}
